import * as React from "react";
import { Input } from "antd";
import { Button } from "../../common/generic/Button";
import * as moment from "moment";
import * as style from "./CreateOfficialClubDialog.Styles";

// Flow Types and Enums
enum DialogFlow {
    CREATE_CLUB = "create-club",
    TRANSFER_OWNERSHIP = "transfer-ownership",
    VALIDATE_TITLE = "validate-title"
}

enum ClubCreationStep {
    CREATING_CLUB = "creating-club",
    COMPLETE = "complete"
}

enum OwnershipTransferStep {
    INPUT = "input",
    VALIDATING_GAMERTAG = "validating-gamertag",
    ADDING_MODERATOR = "adding-moderator",
    TRANSFERRING_OWNERSHIP = "transferring-ownership",
    COMPLETE = "complete"
}

interface CreateOfficialClubDialogProps {
    onClose: () => void;
    onCreateClub: (clubName: string) => Promise<{success: boolean, clubId?: string, error?: string}>;
    onValidateGamertag: (gamertag: string) => Promise<{xuid: string, gamertag: string, displayPic: string}>;
    onValidateClubId: (clubId: string) => Promise<{
        success: boolean;
        result?: {
            clubId: string;
            clubName: string;
            ownerGamertag: string;
            clubPicture?: string;
            description?: string;
            ownerDisplayPicRaw?: string;
        };
        error?: string;
    }>;
    onTransferOwnership: (clubId: string, targetXuid: string) => Promise<{success: boolean, error?: string}>;
    onValidateTitleId: (titleId: string) => Promise<any>;
    initialFlow?: DialogFlow;
}

interface ValidationState {
    isValid: boolean;
    isValidating: boolean;
    error?: string;
    result?: any;
}

interface ClubCreationState {
    clubName: string;
    creationStep?: ClubCreationStep;
    createdClubId?: string;
    showCopyFeedback: boolean;
}

interface TitleValidationState {
    titleId: string;
    validation: ValidationState;
    titleImageLoadError: boolean;
}

interface OwnershipTransferState {
    clubId: string;
    clubValidation: ValidationState;
    gamertag: string;
    gamertagValidation: ValidationState;
    step: OwnershipTransferStep;
    transferResult?: {success: boolean, error?: string};
    displayPicLoadError: boolean;
    ownerDisplayPicLoadError: boolean;
    showCopyFeedback: boolean;
}

interface CreateOfficialClubDialogState {
    selectedFlow: DialogFlow | null;
    isProcessing: boolean;
    statusMessage: string;
    isError: boolean;
    clubCreation: ClubCreationState;
    ownershipTransfer: OwnershipTransferState;
    titleValidation: TitleValidationState;
}

export class CreateOfficialClubDialog extends React.Component<CreateOfficialClubDialogProps, CreateOfficialClubDialogState> {
    private copyFeedbackTimeoutId: number | null = null;
    private abortController: AbortController | null = null;

    constructor(props: CreateOfficialClubDialogProps) {
        super(props);
        this.state = this.getInitialState(props);
    }

    componentDidMount() {
        this.validateProps();
    }

    componentWillUnmount() {
        if (this.copyFeedbackTimeoutId) {
            clearTimeout(this.copyFeedbackTimeoutId);
        }
        if (this.abortController) {
            this.abortController.abort();
        }
    }

    handleClose = () => {
        this.props.onClose();
    }

    formatReleaseDate = (dateString: string) => {
        return moment(dateString).format("MMMM DD, YYYY");
    }

    handleTitleImageError = () => {
        this.setState({
            titleValidation: {
                ...this.state.titleValidation,
                titleImageLoadError: true
            }
        });
    }

    handleDisplayPicError = () => {
        this.setState({
            ownershipTransfer: {
                ...this.state.ownershipTransfer,
                displayPicLoadError: true
            }
        });
    }

    handleOwnerDisplayPicError = () => {
        this.setState({
            ownershipTransfer: {
                ...this.state.ownershipTransfer,
                ownerDisplayPicLoadError: true
            }
        });
    }

    renderClubValidationCard = (clubInfo: {
        clubId: string;
        clubName: string;
        ownerGamertag: string;
        clubPicture?: string;
        description?: string;
        ownerDisplayPicRaw?: string;
    }) => {
        const { ownerDisplayPicLoadError } = this.state.ownershipTransfer;

        return (
            <style.ClubValidationCard>
                {clubInfo.clubPicture ? (
                    <style.ClubPicture
                        src={clubInfo.clubPicture}
                        alt={`${clubInfo.clubName} club picture`}
                    />
                ) : (
                    <style.ClubPicturePlaceholder>
                        No Image
                    </style.ClubPicturePlaceholder>
                )}
                <style.ClubInfo>
                    <style.ClubName>✓ Club Confirmed: {clubInfo.clubName}</style.ClubName>

                    {/* Add club description */}
                    {clubInfo.description && (
                        <style.ClubDescription>{clubInfo.description}</style.ClubDescription>
                    )}

                    {/* Enhanced owner info with display picture */}
                    <style.OwnerSection>
                        <style.BoldText>Current Owner:</style.BoldText>
                        {!ownerDisplayPicLoadError && clubInfo.ownerDisplayPicRaw ? (
                            <style.OwnerDisplayPic
                                src={clubInfo.ownerDisplayPicRaw}
                                alt={`${clubInfo.ownerGamertag}'s avatar`}
                                onError={this.handleOwnerDisplayPicError}
                            />
                        ) : (
                            <style.OwnerDisplayPicPlaceholder>
                                No Avatar
                            </style.OwnerDisplayPicPlaceholder>
                        )}
                        <style.OwnerInfo>
                            {clubInfo.ownerGamertag}
                        </style.OwnerInfo>
                    </style.OwnerSection>

                    {/* Remove Club ID display as requested */}
                </style.ClubInfo>
            </style.ClubValidationCard>
        );
    }

    renderGamertagValidationCard = (gamertagInfo: {xuid: string, gamertag: string, displayPic: string}) => {
        const { displayPicLoadError } = this.state.ownershipTransfer;

        return (
            <style.GamertagValidationCard>
                {!displayPicLoadError && gamertagInfo.displayPic ? (
                    <style.DisplayPic
                        src={gamertagInfo.displayPic}
                        alt={`${gamertagInfo.gamertag}'s avatar`}
                        onError={this.handleDisplayPicError}
                    />
                ) : (
                    <style.DisplayPicPlaceholder>
                        No Image
                    </style.DisplayPicPlaceholder>
                )}
                <style.GamertagInfo>
                    <style.GamertagName>✓ Gamertag Confirmed: {gamertagInfo.gamertag}</style.GamertagName>
                    <style.XuidInfo><style.BoldText>XUID:</style.BoldText> {gamertagInfo.xuid}</style.XuidInfo>
                </style.GamertagInfo>
            </style.GamertagValidationCard>
        );
    }

    renderTitleInfoCard = (titleInfo: any) => {
        const { titleImageLoadError } = this.state.titleValidation;

        return (
            <style.TitleInfoCard>
                {!titleImageLoadError ? (
                    <style.TitleImage
                        src={titleInfo.displayImage}
                        alt={titleInfo.name}
                        onError={this.handleTitleImageError}
                    />
                ) : (
                    <style.TitleImagePlaceholder>
                        No Image
                    </style.TitleImagePlaceholder>
                )}
                <style.TitleDetails>
                    <style.TitleName>{titleInfo.name}</style.TitleName>
                    <style.TitleMeta>
                        <style.TitleMetaItem><style.BoldText>Publisher:</style.BoldText> {titleInfo.publisherName}</style.TitleMetaItem>
                        <style.TitleMetaItem><style.BoldText>Release Date:</style.BoldText> {this.formatReleaseDate(titleInfo.releaseDate)}</style.TitleMetaItem>
                    </style.TitleMeta>
                    <style.TitleDescription>{titleInfo.shortDescription}</style.TitleDescription>
                </style.TitleDetails>
            </style.TitleInfoCard>
        );
    }

    // Flow Selection and Management Methods
    handleFlowSelection = (flow: DialogFlow) => {
        this.setState({ selectedFlow: flow });
    }

    handleBackToFlowSelection = () => {
        this.setState({
            selectedFlow: null,
            isProcessing: false,
            statusMessage: "",
            isError: false
        });
    }

    // Club Creation Flow Methods
    handleClubCreationClubNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({
            clubCreation: {
                ...this.state.clubCreation,
                clubName: e.target.value
            }
        });
    }

    // Title Validation Flow Methods
    handleTitleValidationTitleIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({
            titleValidation: {
                ...this.state.titleValidation,
                titleId: e.target.value,
                validation: { isValid: false, isValidating: false },
                titleImageLoadError: false
            }
        });
    }

    handleValidateTitleId = async () => {
        const { titleValidation } = this.state;
        if (!titleValidation.titleId.trim()) {
            return;
        }

        this.setState({
            titleValidation: {
                ...titleValidation,
                validation: { isValid: false, isValidating: true },
                titleImageLoadError: false
            }
        });

        try {
            const controller = this.cancelCurrentAsyncOperations();
            const result = await this.props.onValidateTitleId(titleValidation.titleId.trim());

            // Check if operation was cancelled - use captured controller
            if (controller.signal.aborted) {
                return;
            }

            this.setState({
                titleValidation: {
                    ...this.state.titleValidation,
                    validation: {
                        isValid: true,
                        isValidating: false,
                        result: result.data
                    }
                }
            });
        } catch (error) {
            // Check if operation was cancelled
            if (this.abortController?.signal.aborted) {
                return;
            }

            this.setState({
                titleValidation: {
                    ...this.state.titleValidation,
                    validation: {
                        isValid: false,
                        isValidating: false,
                        error: error instanceof Error ? error.message : "Validation failed"
                    }
                }
            });
        }
    }

    handleCreateClub = async () => {
        const { clubCreation } = this.state;

        this.setState({
            isProcessing: true,
            clubCreation: {
                ...clubCreation,
                creationStep: ClubCreationStep.CREATING_CLUB
            },
            statusMessage: "",
            isError: false
        });

        try {
            const controller = this.cancelCurrentAsyncOperations();

            const result = await this.props.onCreateClub(clubCreation.clubName.trim());

            // Check if operation was cancelled - use captured controller
            if (controller.signal.aborted) {
                return;
            }

            if (result.success && result.clubId) {
                // Complete
                this.setState({
                    clubCreation: {
                        ...this.state.clubCreation,
                        creationStep: ClubCreationStep.COMPLETE,
                        createdClubId: result.clubId
                    },
                    statusMessage: "Success! Club has been created.",
                    isError: false,
                    isProcessing: false
                });
            } else {
                this.setState({
                    statusMessage: result.error || "Club creation failed",
                    isError: true,
                    isProcessing: false,
                    clubCreation: {
                        ...this.state.clubCreation,
                        creationStep: undefined
                    }
                });
            }

        } catch (error) {
            // Check if operation was cancelled
            if (this.abortController?.signal.aborted) {
                return;
            }

            this.setState({
                statusMessage: error instanceof Error ? error.message : "Club creation failed",
                isError: true,
                isProcessing: false,
                clubCreation: {
                    ...this.state.clubCreation,
                    creationStep: undefined
                }
            });
        }
    }

    isClubCreationEnabled = () => {
        const { clubCreation, isProcessing } = this.state;
        return !isProcessing &&
               clubCreation.clubName.trim().length > 0;
    }

    getClubCreationStepStatus = (step: ClubCreationStep) => {
        const { clubCreation } = this.state;
        const currentStep = clubCreation.creationStep;
        if (!currentStep) {
            return { isActive: false, isComplete: false };
        }

        const steps = [
            ClubCreationStep.CREATING_CLUB,
            ClubCreationStep.COMPLETE
        ];

        const currentIndex = steps.indexOf(currentStep);
        const stepIndex = steps.indexOf(step);

        return {
            isActive: stepIndex === currentIndex,
            isComplete: stepIndex < currentIndex || currentStep === ClubCreationStep.COMPLETE
        };
    }

    renderClubIdDisplayForCreation = () => {
        const { clubCreation } = this.state;

        if (!clubCreation.createdClubId) {
            return null;
        }

        return (
            <style.ClubIdContainer>
                <style.ClubIdLabel>Club ID</style.ClubIdLabel>
                <style.ClubIdDisplay>
                    <style.ClubIdText>{clubCreation.createdClubId}</style.ClubIdText>
                    <style.CopyButton
                        buttonType="default"
                        onClick={() => this.handleCopyClubIdForCreation(clubCreation.createdClubId!)}
                    >
                        Copy
                    </style.CopyButton>
                </style.ClubIdDisplay>
                <style.CopyFeedback isVisible={clubCreation.showCopyFeedback}>
                    ✓ Copied to clipboard
                </style.CopyFeedback>
            </style.ClubIdContainer>
        );
    }

    handleCopyClubIdForCreation = async (clubId: string) => {
        try {
            await navigator.clipboard.writeText(clubId);
            this.setState({
                clubCreation: {
                    ...this.state.clubCreation,
                    showCopyFeedback: true
                }
            });

            this.copyFeedbackTimeoutId = setTimeout(() => {
                this.setState({
                    clubCreation: {
                        ...this.state.clubCreation,
                        showCopyFeedback: false
                    }
                });
            }, 2000) as unknown as number;
        } catch (error) {
            console.error("Failed to copy Club ID:", error);
            // Fallback for older browsers
            const textArea = document.createElement("textarea");
            textArea.value = clubId;
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand("copy");
            document.body.removeChild(textArea);

            this.setState({
                clubCreation: {
                    ...this.state.clubCreation,
                    showCopyFeedback: true
                }
            });
            this.copyFeedbackTimeoutId = setTimeout(() => {
                this.setState({
                    clubCreation: {
                        ...this.state.clubCreation,
                        showCopyFeedback: false
                    }
                });
            }, 2000) as unknown as number;
        }
    }

    // Ownership Transfer Flow Methods
    handleOwnershipTransferClubIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({
            ownershipTransfer: {
                ...this.state.ownershipTransfer,
                clubId: e.target.value,
                clubValidation: { isValid: false, isValidating: false },
                ownerDisplayPicLoadError: false  // Reset error state
            }
        });
    }

    handleValidateClubId = async () => {
        const { ownershipTransfer } = this.state;
        const { clubId } = ownershipTransfer;

        if (!clubId.trim()) {
            return;
        }

        this.setState({
            ownershipTransfer: {
                ...ownershipTransfer,
                clubValidation: { isValid: false, isValidating: true }
            }
        });

        try {
            const response = await this.props.onValidateClubId(clubId.trim());

            if (response.success && response.result) {
                this.setState({
                    ownershipTransfer: {
                        ...this.state.ownershipTransfer,
                        clubValidation: {
                            isValid: true,
                            isValidating: false,
                            result: response.result
                        }
                    }
                });
            } else {
                this.setState({
                    ownershipTransfer: {
                        ...this.state.ownershipTransfer,
                        clubValidation: {
                            isValid: false,
                            isValidating: false,
                            error: response.error || "Club validation failed"
                        }
                    }
                });
            }
        } catch (error) {
            this.setState({
                ownershipTransfer: {
                    ...this.state.ownershipTransfer,
                    clubValidation: {
                        isValid: false,
                        isValidating: false,
                        error: error instanceof Error ? error.message : "Club validation failed"
                    }
                }
            });
        }
    }

    handleOwnershipTransferGamertagChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({
            ownershipTransfer: {
                ...this.state.ownershipTransfer,
                gamertag: e.target.value,
                gamertagValidation: { isValid: false, isValidating: false },
                displayPicLoadError: false
            }
        });
    }

    handleValidateOwnershipTransferGamertag = async () => {
        const { ownershipTransfer } = this.state;
        if (!ownershipTransfer.gamertag.trim()) {
            return;
        }

        this.setState({
            ownershipTransfer: {
                ...ownershipTransfer,
                gamertagValidation: { isValid: false, isValidating: true }
            }
        });

        try {
            const controller = this.cancelCurrentAsyncOperations();
            const result = await this.props.onValidateGamertag(ownershipTransfer.gamertag.trim());

            // Check if operation was cancelled - use captured controller
            if (controller.signal.aborted) {
                return;
            }

            this.setState({
                ownershipTransfer: {
                    ...this.state.ownershipTransfer,
                    gamertagValidation: {
                        isValid: true,
                        isValidating: false,
                        result: result
                    }
                }
            });
        } catch (error) {
            // Check if operation was cancelled
            if (this.abortController?.signal.aborted) {
                return;
            }

            this.setState({
                ownershipTransfer: {
                    ...this.state.ownershipTransfer,
                    gamertagValidation: {
                        isValid: false,
                        isValidating: false,
                        error: error instanceof Error ? error.message : "Validation failed"
                    }
                }
            });
        }
    }

    handleTransferOwnership = async () => {
        const { ownershipTransfer } = this.state;

        const xuid = ownershipTransfer.gamertagValidation.result?.xuid;
        if (!xuid) {
            this.setState({
                statusMessage: "XUID not found. Please validate the gamertag first.",
                isError: true
            });
            return;
        }

        this.setState({
            isProcessing: true,
            ownershipTransfer: {
                ...ownershipTransfer,
                step: OwnershipTransferStep.VALIDATING_GAMERTAG
            },
            statusMessage: "",
            isError: false
        });

        try {
            const controller = this.cancelCurrentAsyncOperations();

            // Step 1: Validate Gamertag
            await new Promise(resolve => setTimeout(resolve, 500));

            // Check if operation was cancelled - use captured controller
            if (controller.signal.aborted) {
                return;
            }

            // Step 2: Add Moderator
            this.setState({
                ownershipTransfer: {
                    ...this.state.ownershipTransfer,
                    step: OwnershipTransferStep.ADDING_MODERATOR
                }
            });
            await new Promise(resolve => setTimeout(resolve, 800));

            // Check if operation was cancelled - use captured controller
            if (controller.signal.aborted) {
                return;
            }

            // Step 3: Transfer Ownership
            this.setState({
                ownershipTransfer: {
                    ...this.state.ownershipTransfer,
                    step: OwnershipTransferStep.TRANSFERRING_OWNERSHIP
                }
            });

            const result = await this.props.onTransferOwnership(ownershipTransfer.clubId.trim(), xuid);

            // Check if operation was cancelled - use captured controller
            if (controller.signal.aborted) {
                return;
            }

            // Step 4: Complete
            this.setState({
                ownershipTransfer: {
                    ...this.state.ownershipTransfer,
                    step: OwnershipTransferStep.COMPLETE,
                    transferResult: result
                },
                statusMessage: result.success ? "Success! Ownership has been transferred." : result.error || "Transfer failed",
                isError: !result.success,
                isProcessing: false
            });

        } catch (error) {
            // Check if operation was cancelled
            if (this.abortController?.signal.aborted) {
                return;
            }

            this.setState({
                statusMessage: error instanceof Error ? error.message : "Transfer failed",
                isError: true,
                isProcessing: false,
                ownershipTransfer: {
                    ...this.state.ownershipTransfer,
                    step: OwnershipTransferStep.INPUT
                }
            });
        }
    }

    isOwnershipTransferEnabled = () => {
        const { ownershipTransfer, isProcessing } = this.state;
        return !isProcessing &&
               ownershipTransfer.step === OwnershipTransferStep.INPUT &&
               ownershipTransfer.clubValidation.isValid &&
               ownershipTransfer.gamertagValidation.isValid &&
               ownershipTransfer.clubId.trim().length > 0;
    }

    getOwnershipTransferStepStatus = (step: OwnershipTransferStep) => {
        const { ownershipTransfer } = this.state;
        const currentStep = ownershipTransfer.step;
        if (!currentStep || currentStep === OwnershipTransferStep.INPUT) {
            return { isActive: false, isComplete: false };
        }

        const steps = [
            OwnershipTransferStep.VALIDATING_GAMERTAG,
            OwnershipTransferStep.ADDING_MODERATOR,
            OwnershipTransferStep.TRANSFERRING_OWNERSHIP,
            OwnershipTransferStep.COMPLETE
        ];

        const currentIndex = steps.indexOf(currentStep);
        const stepIndex = steps.indexOf(step);

        return {
            isActive: stepIndex === currentIndex,
            isComplete: stepIndex < currentIndex || currentStep === OwnershipTransferStep.COMPLETE
        };
    }

    // Flow Rendering Components
    renderFlowSelector = () => {
        return (
            <style.FlowSelectorContainer>
                <style.FlowSelectorTitle>Choose what you'd like to do:</style.FlowSelectorTitle>
                <style.FlowOptionsContainer>
                    <style.FlowOption onClick={() => this.handleFlowSelection(DialogFlow.CREATE_CLUB)}>
                        <style.FlowIcon>🆕</style.FlowIcon>
                        <style.FlowOptionContent>
                            <style.FlowOptionTitle>Create New Club</style.FlowOptionTitle>
                            <style.FlowOptionDescription>Create a club with only a club name</style.FlowOptionDescription>
                        </style.FlowOptionContent>
                    </style.FlowOption>
                    <style.FlowOption onClick={() => this.handleFlowSelection(DialogFlow.TRANSFER_OWNERSHIP)}>
                        <style.FlowIcon>🔄</style.FlowIcon>
                        <style.FlowOptionContent>
                            <style.FlowOptionTitle>Transfer Ownership</style.FlowOptionTitle>
                            <style.FlowOptionDescription>Transfer an existing club to another user</style.FlowOptionDescription>
                        </style.FlowOptionContent>
                    </style.FlowOption>
                    <style.FlowOption onClick={() => this.handleFlowSelection(DialogFlow.VALIDATE_TITLE)}>
                        <style.FlowIcon>🔍</style.FlowIcon>
                        <style.FlowOptionContent>
                            <style.FlowOptionTitle>Validate Title ID</style.FlowOptionTitle>
                            <style.FlowOptionDescription>Look up and validate game title information</style.FlowOptionDescription>
                        </style.FlowOptionContent>
                    </style.FlowOption>
                </style.FlowOptionsContainer>
            </style.FlowSelectorContainer>
        );
    }

    renderClubCreationFlow = () => {
        const { clubCreation, isProcessing } = this.state;

        return (
            <style.FlowContainer>
                <style.FlowHeader>
                    <style.BackButton onClick={this.handleBackToFlowSelection}>← Back</style.BackButton>
                    <style.FlowTitle>Create New Club</style.FlowTitle>
                </style.FlowHeader>

                {/* Club Name Field */}
                <div>
                    <style.Label htmlFor="clubName">Club Name</style.Label>
                    <Input
                        id="clubName"
                        value={clubCreation.clubName}
                        onChange={this.handleClubCreationClubNameChange}
                        disabled={isProcessing}
                        placeholder="Enter Club Name"
                    />
                </div>

                {/* Progress Steps */}
                {clubCreation.creationStep && (
                    <style.ProgressContainer>
                        <style.ProgressStep {...this.getClubCreationStepStatus(ClubCreationStep.CREATING_CLUB)}>
                            {this.getClubCreationStepStatus(ClubCreationStep.CREATING_CLUB).isComplete ? "✓" : "•"} Creating Club
                        </style.ProgressStep>
                        <style.ProgressStep {...this.getClubCreationStepStatus(ClubCreationStep.COMPLETE)}>
                            {this.getClubCreationStepStatus(ClubCreationStep.COMPLETE).isComplete ? "✓" : "•"} Complete
                        </style.ProgressStep>
                    </style.ProgressContainer>
                )}

                {/* Club ID Display */}
                {clubCreation.createdClubId && this.renderClubIdDisplayForCreation()}

                {/* Buttons */}
                <style.ButtonContainer>
                    <Button
                        buttonType="default"
                        onClick={this.handleClose}
                        disabled={isProcessing && clubCreation.creationStep !== ClubCreationStep.COMPLETE}
                    >
                        Close
                    </Button>
                    {clubCreation.creationStep !== ClubCreationStep.COMPLETE && (
                        <Button
                            buttonType="primary"
                            onClick={this.handleCreateClub}
                            disabled={!this.isClubCreationEnabled()}
                        >
                            {isProcessing ? "Creating..." : "Create Club"}
                        </Button>
                    )}
                </style.ButtonContainer>
            </style.FlowContainer>
        );
    }

    renderTitleValidationFlow = () => {
        const { titleValidation, isProcessing } = this.state;

        return (
            <style.FlowContainer>
                <style.FlowHeader>
                    <style.BackButton onClick={this.handleBackToFlowSelection}>← Back</style.BackButton>
                    <style.FlowTitle>Validate Title ID</style.FlowTitle>
                </style.FlowHeader>

                {/* Title ID Field */}
                <div>
                    <style.Label htmlFor="titleId">Title ID</style.Label>
                    <style.ValidationContainer>
                        <Input
                            id="titleId"
                            value={titleValidation.titleId}
                            onChange={this.handleTitleValidationTitleIdChange}
                            disabled={isProcessing}
                            placeholder="Enter Title ID"
                            style={{ flex: 1 }}
                        />
                        <style.ValidationButton
                            buttonType="default"
                            onClick={this.handleValidateTitleId}
                            disabled={!titleValidation.titleId.trim() || titleValidation.validation.isValidating || isProcessing}
                        >
                            {titleValidation.validation.isValidating ? "..." : "Validate"}
                        </style.ValidationButton>
                    </style.ValidationContainer>
                    {titleValidation.validation.isValid && titleValidation.validation.result && (
                        this.renderTitleInfoCard(titleValidation.validation.result)
                    )}
                    {titleValidation.validation.error && (
                        <style.ValidationStatus isError={true}>
                            ✗ {titleValidation.validation.error}
                        </style.ValidationStatus>
                    )}
                </div>

                {/* Buttons */}
                <style.ButtonContainer>
                    <Button
                        buttonType="default"
                        onClick={this.handleBackToFlowSelection}
                    >
                        Back
                    </Button>
                    <Button
                        buttonType="default"
                        onClick={this.handleClose}
                    >
                        Close
                    </Button>
                </style.ButtonContainer>
            </style.FlowContainer>
        );
    }

    renderOwnershipTransferFlow = () => {
        const { ownershipTransfer, isProcessing } = this.state;

        return (
            <style.FlowContainer>
                <style.FlowHeader>
                    <style.BackButton onClick={this.handleBackToFlowSelection}>← Back</style.BackButton>
                    <style.FlowTitle>Transfer Club Ownership</style.FlowTitle>
                </style.FlowHeader>

                {/* Club ID Field with validation */}
                <div>
                    <style.Label htmlFor="clubId">Club ID</style.Label>
                    <style.ValidationContainer>
                        <Input
                            id="clubId"
                            value={ownershipTransfer.clubId}
                            onChange={this.handleOwnershipTransferClubIdChange}
                            disabled={ownershipTransfer.step !== OwnershipTransferStep.INPUT}
                            placeholder="Enter Club ID"
                            style={{ flex: 1 }}
                        />
                        <style.ValidationButton
                            buttonType="default"
                            onClick={this.handleValidateClubId}
                            disabled={!ownershipTransfer.clubId.trim() || ownershipTransfer.clubValidation.isValidating}
                        >
                            {ownershipTransfer.clubValidation.isValidating ? "..." : "Validate"}
                        </style.ValidationButton>
                    </style.ValidationContainer>

                    {/* Club validation result display */}
                    {ownershipTransfer.clubValidation.isValid && ownershipTransfer.clubValidation.result && (
                        this.renderClubValidationCard(ownershipTransfer.clubValidation.result)
                    )}
                    {ownershipTransfer.clubValidation.error && (
                        <style.ValidationStatus isError={true}>
                            ✗ {ownershipTransfer.clubValidation.error}
                        </style.ValidationStatus>
                    )}
                </div>

                {/* Gamertag Field */}
                <div>
                    <style.Label htmlFor="gamertag">Gamertag</style.Label>
                    <style.ValidationContainer>
                        <Input
                            id="gamertag"
                            value={ownershipTransfer.gamertag}
                            onChange={this.handleOwnershipTransferGamertagChange}
                            disabled={isProcessing}
                            placeholder="Enter Gamertag"
                            style={{ flex: 1 }}
                        />
                        <style.ValidationButton
                            buttonType="default"
                            onClick={this.handleValidateOwnershipTransferGamertag}
                            disabled={!ownershipTransfer.gamertag.trim() || ownershipTransfer.gamertagValidation.isValidating || isProcessing}
                        >
                            {ownershipTransfer.gamertagValidation.isValidating ? "..." : "Validate"}
                        </style.ValidationButton>
                    </style.ValidationContainer>
                    {ownershipTransfer.gamertagValidation.isValid && ownershipTransfer.gamertagValidation.result && (
                        this.renderGamertagValidationCard(ownershipTransfer.gamertagValidation.result)
                    )}
                    {ownershipTransfer.gamertagValidation.error && (
                        <style.ValidationStatus isError={true}>
                            ✗ {ownershipTransfer.gamertagValidation.error}
                        </style.ValidationStatus>
                    )}
                </div>

                {/* Progress Steps */}
                {ownershipTransfer.step && ownershipTransfer.step !== OwnershipTransferStep.INPUT && (
                    <style.ProgressContainer>
                        <style.ProgressStep {...this.getOwnershipTransferStepStatus(OwnershipTransferStep.VALIDATING_GAMERTAG)}>
                            {this.getOwnershipTransferStepStatus(OwnershipTransferStep.VALIDATING_GAMERTAG).isComplete ? "✓" : "•"} Validating Gamertag
                        </style.ProgressStep>
                        <style.ProgressStep {...this.getOwnershipTransferStepStatus(OwnershipTransferStep.ADDING_MODERATOR)}>
                            {this.getOwnershipTransferStepStatus(OwnershipTransferStep.ADDING_MODERATOR).isComplete ? "✓" : "•"} Adding Moderator
                        </style.ProgressStep>
                        <style.ProgressStep {...this.getOwnershipTransferStepStatus(OwnershipTransferStep.TRANSFERRING_OWNERSHIP)}>
                            {this.getOwnershipTransferStepStatus(OwnershipTransferStep.TRANSFERRING_OWNERSHIP).isComplete ? "✓" : "•"} Transferring Ownership
                        </style.ProgressStep>
                        <style.ProgressStep {...this.getOwnershipTransferStepStatus(OwnershipTransferStep.COMPLETE)}>
                            {this.getOwnershipTransferStepStatus(OwnershipTransferStep.COMPLETE).isComplete ? "✓" : "•"} Complete
                        </style.ProgressStep>
                    </style.ProgressContainer>
                )}

                {/* Buttons */}
                <style.ButtonContainer>
                    <Button
                        buttonType="default"
                        onClick={this.handleClose}
                        disabled={isProcessing && ownershipTransfer.step !== OwnershipTransferStep.COMPLETE}
                    >
                        Close
                    </Button>
                    {ownershipTransfer.step !== OwnershipTransferStep.COMPLETE && (
                        <Button
                            buttonType="primary"
                            onClick={this.handleTransferOwnership}
                            disabled={!this.isOwnershipTransferEnabled()}
                        >
                            {isProcessing ? "Transferring..." : "Transfer Ownership"}
                        </Button>
                    )}
                </style.ButtonContainer>
            </style.FlowContainer>
        );
    }

    render() {
        const {
            selectedFlow,
            isProcessing,
            statusMessage,
            isError
        } = this.state;

        // Determine modal title based on flow
        const getModalTitle = () => {
            if (selectedFlow === DialogFlow.CREATE_CLUB) {
                return "Create New Club";
            } else if (selectedFlow === DialogFlow.TRANSFER_OWNERSHIP) {
                return "Transfer Club Ownership";
            } else if (selectedFlow === DialogFlow.VALIDATE_TITLE) {
                return "Validate Title ID";
            }
            return "Club Management";
        };

        // Determine if modal can be closed
        const canCloseModal = !isProcessing;

        return (
            <style.StyledModal
                title={getModalTitle()}
                visible={true}
                onCancel={this.handleClose}
                maskClosable={canCloseModal}
                closable={canCloseModal}
            >
                {/* Render content based on selected flow */}
                {!selectedFlow && this.renderFlowSelector()}
                {selectedFlow === DialogFlow.CREATE_CLUB && this.renderClubCreationFlow()}
                {selectedFlow === DialogFlow.TRANSFER_OWNERSHIP && this.renderOwnershipTransferFlow()}
                {selectedFlow === DialogFlow.VALIDATE_TITLE && this.renderTitleValidationFlow()}

                {/* Global Status Message */}
                {statusMessage && selectedFlow && (
                    <style.StatusMessage isError={isError}>
                        {statusMessage}
                    </style.StatusMessage>
                )}
            </style.StyledModal>
        );
    }

        private getInitialState(props: CreateOfficialClubDialogProps): CreateOfficialClubDialogState {
        return {
            // New flow-based state
            selectedFlow: null,
            isProcessing: false,
            statusMessage: "",
            isError: false,

            // Flow-specific states
            clubCreation: {
                clubName: "",
                creationStep: undefined,
                createdClubId: undefined,
                showCopyFeedback: false
            },
            titleValidation: {
                titleId: "",
                validation: { isValid: false, isValidating: false },
                titleImageLoadError: false
            },
            ownershipTransfer: {
                clubId: "",
                clubValidation: { isValid: false, isValidating: false },
                gamertag: "",
                gamertagValidation: { isValid: false, isValidating: false },
                step: OwnershipTransferStep.INPUT,
                transferResult: undefined,
                displayPicLoadError: false,
                ownerDisplayPicLoadError: false,
                showCopyFeedback: false
            }
        };
    }

    private cancelCurrentAsyncOperations(): AbortController {
        if (this.abortController) {
            this.abortController.abort();
        }
        this.abortController = new AbortController();
        return this.abortController;
    }

    private validateProps() {
        const { onValidateTitleId, onValidateGamertag } = this.props;

        if (!onValidateTitleId) {
            console.error("CreateOfficialClubDialog: onValidateTitleId prop is required");
            this.setState({
                statusMessage: "Configuration error: Title validation not available",
                isError: true
            });
            return;
        }

        if (!onValidateGamertag) {
            console.error("CreateOfficialClubDialog: onValidateGamertag prop is required");
            this.setState({
                statusMessage: "Configuration error: Gamertag validation not available",
                isError: true
            });
            return;
        }
    }
}
